export interface CategorySeoContent {
  key: string;
  label: string;
  headline: string;
  intro: string;
  liveEventsHeading: string;
  liveEvents: string;
  howToWatchHeading: string;
  howToWatch: string[];
  coverageHeading: string;
  coverage: string;
  whyHeading: string;
  whyPoints: string[];
  closing: string;
  metaDescription: string;
}

const SHARED_WHY = [
  'Free HD live streams with no signup or subscription',
  'Multiple streamer links so you can switch if one buffers',
  'Updated fixtures and kick-off times for today’s live events',
  'Works on mobile, tablet, and desktop browsers',
];

function buildContent(
  partial: Omit<CategorySeoContent, 'whyHeading' | 'whyPoints'> & {
    whyPoints?: string[];
  },
): CategorySeoContent {
  return {
    whyHeading: `Why Watch ${partial.label} Live on Buffstreams`,
    whyPoints: partial.whyPoints || SHARED_WHY,
    ...partial,
  };
}

const CATEGORY_SEO: Record<string, CategorySeoContent> = {
  SOCCER: buildContent({
    key: 'SOCCER',
    label: 'Soccer',
    headline: 'Watch Free Soccer Live Streams & Football Matches Today',
    intro:
      'Buffstreams is your free hub for soccer live streams and football fixtures. Catch Premier League, Champions League, La Liga, Serie A, Bundesliga, and international live matches in HD — no account required. Browse today’s soccer schedule above and open any fixture for free streaming links.',
    liveEventsHeading: 'Live Soccer Events & Match Streams',
    liveEvents:
      'Follow live soccer events as they happen: weekend league fixtures, midweek Champions League nights, cup ties, and international friendlies. Our soccer category lists upcoming and live football matches with real-time status so you never miss a kick-off, goal, or late winner.',
    howToWatchHeading: 'How to Watch Soccer Streams Free on Buffstreams',
    howToWatch: [
      'Pick a live or upcoming soccer match from the fixtures list above',
      'Open the fixture page for free HD streamer links',
      'If one link buffers, switch players instantly without refreshing',
      'Return here for more live football streams and today’s schedule',
    ],
    coverageHeading: 'Soccer Leagues & Competitions We Cover',
    coverage:
      'Watch free streams for top European leagues, continental club competitions, and national team matches. Whether you follow English Premier League soccer streams, UEFA Champions League football nights, or domestic cups, Buffstreams organises live events and stream links in one place.',
    closing:
      'Stay on Buffstreams for free soccer live streams, updated football fixtures, and HD coverage of the biggest live soccer events — anytime, on any device.',
    metaDescription:
      'Watch free soccer live streams and football fixtures on Buffstreams. Premier League, Champions League and more live matches in HD — no signup.',
  }),

  NFL: buildContent({
    key: 'NFL',
    label: 'NFL',
    headline: 'Free NFL Live Streams – Watch Every Game Online in HD',
    intro:
      'Stream NFL live games free on Buffstreams. From Sunday Night Football and Thursday night kickoffs to playoffs and the Super Bowl, we list NFL fixtures and free live stream links so you can watch every drive without a cable subscription.',
    liveEventsHeading: 'Live NFL Games & Football Events',
    liveEvents:
      'Catch regular-season NFL live streams, primetime matchups, wild-card weekends, and championship games. Our NFL category highlights today’s live American football events with kick-off times and free streaming options for every game on the slate.',
    howToWatchHeading: 'How to Watch NFL Streams Free',
    howToWatch: [
      'Select an NFL game from today’s fixtures above',
      'Open the match page for free live stream players',
      'Switch streamer links if you need a clearer feed',
      'Check back for Sunday, Monday, and Thursday night football streams',
    ],
    coverageHeading: 'NFL Coverage on Buffstreams',
    coverage:
      'Watch free NFL live streams across the regular season, Thanksgiving classics, playoff races, conference championships, and Super Bowl Sunday. Buffstreams keeps NFL schedules and HD stream links updated for fans who want live American football online.',
    closing:
      'Tune in on Buffstreams for free NFL live streams, full game schedules, and HD coverage of the biggest live football events in the USA.',
    metaDescription:
      'Free NFL live streams on Buffstreams. Watch regular season, playoffs and Super Bowl games in HD with updated fixtures — no signup required.',
  }),

  NBA: buildContent({
    key: 'NBA',
    label: 'NBA',
    headline: 'Free NBA Live Streams – Watch Basketball Games in HD',
    intro:
      'Watch NBA live streams free on Buffstreams. Follow regular-season tip-offs, nationally televised marquee games, playoff series, and the NBA Finals with free HD links and an updated basketball fixtures list.',
    liveEventsHeading: 'Live NBA Games & Basketball Events',
    liveEvents:
      'From opening night to Game 7 drama, our NBA category tracks live basketball events and tip-off times. Open any fixture for free NBA stream links and switch players if you need a smoother live feed.',
    howToWatchHeading: 'How to Watch NBA Streams Free',
    howToWatch: [
      'Choose a live or upcoming NBA game from the list above',
      'Open the fixture for free HD basketball stream links',
      'Use multiple players to avoid buffering during crunch time',
      'Return for more NBA live streams throughout the week',
    ],
    coverageHeading: 'NBA Season & Playoff Streaming',
    coverage:
      'Stream free NBA games across the regular season, Christmas Day slate, All-Star weekend context, play-in tournament, and NBA Finals. Buffstreams helps basketball fans watch live events online without paywalls.',
    closing:
      'Watch free NBA live streams and today’s basketball fixtures on Buffstreams — HD coverage of live NBA events, anytime.',
    metaDescription:
      'Watch free NBA live streams on Buffstreams. Regular season, playoffs and Finals basketball games in HD with free fixtures and links.',
  }),

  F1: buildContent({
    key: 'F1',
    label: 'F1',
    headline: 'Free F1 Live Streams – Watch Formula 1 Grand Prix Online',
    intro:
      'Stream Formula 1 live free on Buffstreams. Follow practice, qualifying, and race day Grand Prix coverage with free F1 live stream links and an updated motorsport schedule for every race weekend.',
    liveEventsHeading: 'Live F1 Events & Race Weekends',
    liveEvents:
      'Catch live F1 events from FP1 through the chequered flag. Our F1 category lists Grand Prix weekends so you can watch free Formula 1 streams for qualifying battles, sprint formats where applicable, and full race-day action.',
    howToWatchHeading: 'How to Watch F1 Streams Free',
    howToWatch: [
      'Open the Grand Prix or session listed in fixtures above',
      'Launch a free F1 live stream player on the event page',
      'Switch links if you need a clearer race feed',
      'Stay for more Formula 1 live events across the season',
    ],
    coverageHeading: 'Formula 1 Season Coverage',
    coverage:
      'Watch free F1 live streams for iconic circuits and championship-deciding weekends. Buffstreams organises Formula 1 fixtures and HD stream options so motorsport fans can follow live Grand Prix events online.',
    closing:
      'Experience free F1 live streams and Grand Prix schedules on Buffstreams — live Formula 1 racing without a subscription.',
    metaDescription:
      'Free F1 live streams on Buffstreams. Watch Formula 1 practice, qualifying and Grand Prix races in HD — updated fixtures, no signup.',
  }),

  BOXING: buildContent({
    key: 'BOXING',
    label: 'Boxing',
    headline: 'Free Boxing Live Streams – Watch Fight Night Online in HD',
    intro:
      'Watch boxing live streams free on Buffstreams. From title fights and undercards to major pay-per-view style cards listed with free links, we cover live boxing events and upcoming fight schedules in one place.',
    liveEventsHeading: 'Live Boxing Events & Fight Cards',
    liveEvents:
      'Follow live boxing events as weigh-ins turn into fight night. Our boxing category highlights upcoming and live bouts so you can stream free boxing matches, championship fights, and stacked cards without missing the main event.',
    howToWatchHeading: 'How to Watch Boxing Streams Free',
    howToWatch: [
      'Select a fight or card from the fixtures list above',
      'Open free HD boxing stream links on the event page',
      'Switch players if the feed drops between rounds',
      'Check back for the next live boxing event schedule',
    ],
    coverageHeading: 'Boxing Coverage on Buffstreams',
    coverage:
      'Stream free boxing live events across weight classes and promotions. Whether it is a local title fight or a global stadium card, Buffstreams lists fight schedules and free streaming options for boxing fans.',
    closing:
      'Don’t miss free boxing live streams and fight-night fixtures on Buffstreams — HD coverage of live boxing events online.',
    metaDescription:
      'Free boxing live streams on Buffstreams. Watch fight night, title bouts and upcoming boxing events in HD — no signup required.',
  }),

  UFC: buildContent({
    key: 'UFC',
    label: 'UFC',
    headline: 'Free UFC Live Streams – Watch Fight Night & PPV Events',
    intro:
      'Stream UFC live free on Buffstreams. Catch Fight Night cards, numbered PPV events, prelims, and main events with free UFC live stream links and an updated MMA fight schedule.',
    liveEventsHeading: 'Live UFC Events & MMA Fight Cards',
    liveEvents:
      'Watch live UFC events from early prelims to the main event. Our UFC category tracks fight-night schedules and free streaming links so MMA fans can follow every octagon walkout without a cable package.',
    howToWatchHeading: 'How to Watch UFC Streams Free',
    howToWatch: [
      'Pick a UFC event from the fixtures above',
      'Open free HD streamer links on the fight page',
      'Switch players during busy PPV traffic if needed',
      'Return for the next UFC Fight Night or numbered event',
    ],
    coverageHeading: 'UFC & MMA Streaming Coverage',
    coverage:
      'Free UFC live streams for Fight Nights, international cards, and major PPV spectacles. Buffstreams keeps MMA fixtures and HD links ready for live combat sports events.',
    closing:
      'Watch free UFC live streams and MMA fight schedules on Buffstreams — never miss a live octagon event.',
    metaDescription:
      'Free UFC live streams on Buffstreams. Watch Fight Night and PPV events in HD with free MMA fixtures and stream links.',
  }),

  MMA: buildContent({
    key: 'MMA',
    label: 'MMA',
    headline: 'Free MMA Live Streams – UFC & Combat Sports Events',
    intro:
      'Watch MMA live streams free on Buffstreams, including UFC cards and related combat sports events. Get free HD links and updated fight fixtures for live mixed martial arts nights.',
    liveEventsHeading: 'Live MMA Events Online',
    liveEvents:
      'Follow live MMA events with clear schedules and free stream options. From Fight Night lineups to major championship cards, Buffstreams helps fans watch combat sports live without signup.',
    howToWatchHeading: 'How to Watch MMA Streams Free',
    howToWatch: [
      'Choose a live or upcoming MMA card above',
      'Open the event page for free stream players',
      'Switch links if one feed buffers',
      'Browse more UFC and MMA live events anytime',
    ],
    coverageHeading: 'MMA Coverage on Buffstreams',
    coverage:
      'Stream free MMA and UFC live events with fixtures organised for fight fans. Stay ready for prelims, co-mains, and title fights with HD streaming links.',
    closing:
      'Catch free MMA live streams on Buffstreams — live combat sports and UFC events in HD.',
    metaDescription:
      'Free MMA live streams on Buffstreams. Watch UFC and combat sports fight nights in HD — updated fixtures, free access.',
  }),

  NHL: buildContent({
    key: 'NHL',
    label: 'NHL',
    headline: 'Free NHL Live Streams – Watch Hockey Games Online',
    intro:
      'Stream NHL live games free on Buffstreams. Follow regular-season hockey, outdoor classics context, playoff series, and Stanley Cup nights with free NHL live stream links and updated fixtures.',
    liveEventsHeading: 'Live NHL Games & Hockey Events',
    liveEvents:
      'Watch live NHL events puck-drop to final horn. Our NHL category lists today’s hockey fixtures so you can open free live streams for every matchup on the board.',
    howToWatchHeading: 'How to Watch NHL Streams Free',
    howToWatch: [
      'Select an NHL game from the fixtures above',
      'Launch free HD hockey stream links',
      'Change players if you need a clearer feed',
      'Come back for more live NHL action nightly',
    ],
    coverageHeading: 'NHL Season Streaming',
    coverage:
      'Free NHL live streams across the regular season and postseason. Buffstreams organises hockey schedules and HD links for fans who want live events online.',
    closing:
      'Watch free NHL live streams and hockey fixtures on Buffstreams — live NHL events in HD.',
    metaDescription:
      'Free NHL live streams on Buffstreams. Watch hockey games, playoffs and live NHL events in HD — no signup.',
  }),

  MLB: buildContent({
    key: 'MLB',
    label: 'MLB',
    headline: 'Free MLB Live Streams – Watch Baseball Games in HD',
    intro:
      'Watch MLB live streams free on Buffstreams. From Opening Day and summer nightcaps to October baseball, find free MLB stream links and updated Major League Baseball fixtures.',
    liveEventsHeading: 'Live MLB Games & Baseball Events',
    liveEvents:
      'Catch live MLB events every slate: weekday series, weekend getaways, and postseason showdowns. Open any fixture for free baseball live streams in HD.',
    howToWatchHeading: 'How to Watch MLB Streams Free',
    howToWatch: [
      'Pick a baseball game from today’s MLB fixtures',
      'Open free live stream players on the match page',
      'Switch links between innings if needed',
      'Return for more MLB live streams all season',
    ],
    coverageHeading: 'MLB Coverage on Buffstreams',
    coverage:
      'Stream free MLB games through the regular season, rivalry weekends, and playoff races. Buffstreams keeps baseball fans ready for live events with HD streaming options.',
    closing:
      'Tune into free MLB live streams on Buffstreams — live baseball events and fixtures updated daily.',
    metaDescription:
      'Free MLB live streams on Buffstreams. Watch Major League Baseball games and live events in HD — free fixtures and links.',
  }),

  MLS: buildContent({
    key: 'MLS',
    label: 'MLS',
    headline: 'Free MLS Live Streams – Watch Major League Soccer Online',
    intro:
      'Stream MLS live matches free on Buffstreams. Follow regular-season soccer, derby nights, playoffs, and cup ties with free MLS live stream links and an updated fixtures board.',
    liveEventsHeading: 'Live MLS Matches & Soccer Events',
    liveEvents:
      'Watch live MLS events across the league calendar. Our MLS category lists kick-offs and free streaming options so you can catch Major League Soccer online in HD.',
    howToWatchHeading: 'How to Watch MLS Streams Free',
    howToWatch: [
      'Choose an MLS fixture from the list above',
      'Open free HD soccer stream links',
      'Switch players if one stream lags',
      'Browse more MLS live matches anytime',
    ],
    coverageHeading: 'MLS Season Coverage',
    coverage:
      'Free MLS live streams for regular-season battles, Decision Day context, and playoff pushes. Buffstreams helps soccer fans watch live Major League Soccer events without signup.',
    closing:
      'Watch free MLS live streams and soccer fixtures on Buffstreams — live MLS events in HD.',
    metaDescription:
      'Free MLS live streams on Buffstreams. Watch Major League Soccer matches and live events in HD — no signup required.',
  }),

  MOTOGP: buildContent({
    key: 'MOTOGP',
    label: 'MotoGP',
    headline: 'Free MotoGP Live Streams – Watch Motorcycle Racing Online',
    intro:
      'Stream MotoGP live free on Buffstreams. Follow free practice, qualifying, and race-day MotoGP live streams with updated motorsport fixtures for every Grand Prix weekend.',
    liveEventsHeading: 'Live MotoGP Events & Race Weekends',
    liveEvents:
      'Catch live MotoGP events from warm-up to the flag. Our MotoGP category lists race weekends and free streaming links for premier-class motorcycle racing fans.',
    howToWatchHeading: 'How to Watch MotoGP Streams Free',
    howToWatch: [
      'Open a MotoGP session or race from fixtures above',
      'Start a free HD live stream on the event page',
      'Switch links if you need a clearer onboard-style feed',
      'Return for more MotoGP live events each round',
    ],
    coverageHeading: 'MotoGP Season Streaming',
    coverage:
      'Watch free MotoGP live streams across the world championship calendar. Buffstreams organises race schedules and HD links for live motorcycle Grand Prix events.',
    closing:
      'Ride along with free MotoGP live streams on Buffstreams — live racing events without a subscription.',
    metaDescription:
      'Free MotoGP live streams on Buffstreams. Watch practice, qualifying and Grand Prix races in HD — updated fixtures.',
  }),

  RUGBY: buildContent({
    key: 'RUGBY',
    label: 'Rugby',
    headline: 'Free Rugby Live Streams – Watch Matches & Tournaments Online',
    intro:
      'Watch rugby live streams free on Buffstreams. Follow club fixtures, international tests, and tournament rugby with free HD links and an updated live events schedule.',
    liveEventsHeading: 'Live Rugby Matches & Events',
    liveEvents:
      'Stream live rugby events from kick-off to the final whistle. Our rugby category lists upcoming and live matches so fans can watch free rugby streams without missing big tournament days.',
    howToWatchHeading: 'How to Watch Rugby Streams Free',
    howToWatch: [
      'Select a rugby fixture from the list above',
      'Open free live stream links on the match page',
      'Change players if the feed drops',
      'Check back for more live rugby events',
    ],
    coverageHeading: 'Rugby Coverage on Buffstreams',
    coverage:
      'Free rugby live streams for domestic leagues and international windows. Buffstreams keeps rugby fixtures and HD streaming options ready for live match days.',
    closing:
      'Watch free rugby live streams on Buffstreams — live rugby events and fixtures in HD.',
    metaDescription:
      'Free rugby live streams on Buffstreams. Watch club and international rugby live events in HD — no signup.',
  }),

  TENNIS: buildContent({
    key: 'TENNIS',
    label: 'Tennis',
    headline: 'Free Tennis Live Streams – Watch Matches & Tournaments',
    intro:
      'Stream tennis live free on Buffstreams. Follow ATP, WTA, and Grand Slam–week action with free tennis live stream links and updated match fixtures for today’s courts.',
    liveEventsHeading: 'Live Tennis Events & Match Schedules',
    liveEvents:
      'Catch live tennis events from early rounds to finals. Our tennis category highlights match schedules so you can watch free streams of singles and doubles as they unfold.',
    howToWatchHeading: 'How to Watch Tennis Streams Free',
    howToWatch: [
      'Pick a tennis match from the fixtures above',
      'Open free HD streamer links on the match page',
      'Switch players between sets if needed',
      'Return for more live tennis tournament coverage',
    ],
    coverageHeading: 'Tennis Tournament Streaming',
    coverage:
      'Watch free tennis live streams across tour stops and major championships weeks. Buffstreams organises tennis fixtures and HD links for fans of live court action.',
    closing:
      'Enjoy free tennis live streams on Buffstreams — live tennis events and match schedules in HD.',
    metaDescription:
      'Free tennis live streams on Buffstreams. Watch ATP, WTA and tournament matches live in HD — updated fixtures.',
  }),

  CRICKET: buildContent({
    key: 'CRICKET',
    label: 'Cricket',
    headline: 'Free Cricket Live Streams – PSL, IPL, T20 & International',
    intro:
      'Watch cricket live streams free on Buffstreams. Follow PSL, IPL, T20 leagues, ODIs, and Test match days with free cricket stream links and updated fixtures for live cricket events.',
    liveEventsHeading: 'Live Cricket Matches & Events',
    liveEvents:
      'Stream live cricket events from the first ball to the final over. Our cricket category lists today’s matches so fans can open free HD streams for domestic leagues and international series.',
    howToWatchHeading: 'How to Watch Cricket Streams Free',
    howToWatch: [
      'Choose a cricket fixture from the board above',
      'Open free live stream players on the match page',
      'Switch links during busy T20 nights if needed',
      'Come back for more live cricket events daily',
    ],
    coverageHeading: 'Cricket Coverage on Buffstreams',
    coverage:
      'Free cricket live streams for franchise T20 leagues, bilaterals, and major ICC windows. Buffstreams keeps cricket schedules and streaming options organised for live match days.',
    closing:
      'Watch free cricket live streams on Buffstreams — live cricket events, PSL, IPL context, and more in HD.',
    metaDescription:
      'Free cricket live streams on Buffstreams. Watch PSL, IPL, T20 and international cricket live in HD — no signup.',
  }),

  NASCAR: buildContent({
    key: 'NASCAR',
    label: 'NASCAR',
    headline: 'Free NASCAR Live Streams – Watch Races Online in HD',
    intro:
      'Stream NASCAR live free on Buffstreams. Follow Cup Series race weekends and motorsport fixtures with free NASCAR live stream links for practice, qualifying context, and race day.',
    liveEventsHeading: 'Live NASCAR Events & Race Days',
    liveEvents:
      'Catch live NASCAR events as the field goes green. Our NASCAR category lists race schedules and free streaming options for stock-car fans who want live coverage online.',
    howToWatchHeading: 'How to Watch NASCAR Streams Free',
    howToWatch: [
      'Open a NASCAR race or session from fixtures above',
      'Start a free HD live stream on the event page',
      'Switch players if the feed lags under caution',
      'Return for the next live NASCAR weekend',
    ],
    coverageHeading: 'NASCAR Season Streaming',
    coverage:
      'Watch free NASCAR live streams across the season calendar. Buffstreams helps racing fans follow live events with updated fixtures and HD links.',
    closing:
      'Watch free NASCAR live streams on Buffstreams — live racing events without a subscription.',
    metaDescription:
      'Free NASCAR live streams on Buffstreams. Watch race weekends and live NASCAR events in HD — free fixtures and links.',
  }),
};

/** Normalize route param (Soccer, nfl, mma, MOTOGP) to content key. */
export function normalizeCategoryKey(categoryName?: string): string {
  if (!categoryName) return '';
  const raw = categoryName.trim().toUpperCase();
  if (raw === 'MMA' || raw === 'UFC' || raw === 'MMA/UFC') return raw === 'UFC' ? 'UFC' : 'MMA';
  if (raw === 'MOTOGP' || raw === 'MOTO GP') return 'MOTOGP';
  return raw;
}

export function getCategorySeoContent(categoryName?: string): CategorySeoContent | null {
  const key = normalizeCategoryKey(categoryName);
  if (!key) return null;

  if (CATEGORY_SEO[key]) return CATEGORY_SEO[key];

  // Friendly fallback for unknown categories
  const label = categoryName!.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return buildContent({
    key,
    label,
    headline: `Watch Free ${label} Live Streams on Buffstreams`,
    intro: `Buffstreams offers free ${label} live streams and updated fixtures for today’s live events. Browse the schedule above and open any match for free HD streaming links — no signup required.`,
    liveEventsHeading: `Live ${label} Events`,
    liveEvents: `Follow live ${label} events with kick-off times and free stream options. Buffstreams keeps this category updated so you can watch live sports online in HD.`,
    howToWatchHeading: `How to Watch ${label} Streams Free`,
    howToWatch: [
      `Select a ${label} fixture from the list above`,
      'Open the event page for free HD streamer links',
      'Switch players if one stream buffers',
      `Return for more live ${label} events anytime`,
    ],
    coverageHeading: `${label} Coverage on Buffstreams`,
    coverage: `Stream free ${label} live events with fixtures organised for fans who want fast access to live sports streaming without subscriptions.`,
    closing: `Watch free ${label} live streams on Buffstreams — live events and HD links in one place.`,
    metaDescription: `Watch free ${label} live streams on Buffstreams. Updated fixtures and HD links for live ${label} events — no signup.`,
  });
}
